import { useRef, useState } from "react";
import type { MenuItem } from "primereact/menuitem";
import { BreadCrumb } from "primereact/breadcrumb";
import { IconAlertTriangle, IconArrowLeft, IconCalendar, IconDownload } from "@tabler/icons-react";
import { Card } from "primereact/card";
import { useLocation, useNavigate } from "react-router-dom";
import { Tag } from "primereact/tag";
import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "../services/http.service";
import { APIMapping } from "../constants/api-mapping.cnst";
import { Skeleton } from "primereact/skeleton";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { getSeverity } from "../services/util.service";
import { HBL_PA_RESOURCES } from "../constants/data.cnst";
import { TABLE_COLUMNS_CONFIG } from "../constants/table.cnst";
import Table from "../components/table";

export default function StationEventRCA() {
    const navigate = useNavigate();

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventId');

    const handleNavigateToDashboard = () => {
        const searchParams = new URLSearchParams(location.search);

        // remove eventId
        searchParams.delete("eventId");

        navigate({
            pathname: "/dashboard/station-events",
            search: searchParams.toString(),
        });
    };

    const breadcrumb: MenuItem[] = [
        { label: 'Station Events', command: () => handleNavigateToDashboard(), style: { fontWeight: 500 } },
        { label: 'Root Cause Analysis', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
    ];

    //station event details api
    const { data: eventsResponseData, isFetching: isEventResponseLoading } = useQuery({
        queryKey: ["station-events-data", eventId],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.STATION_EVENTS_RCA,
                pathParams: { eventId: eventId as string },
                resourceName: HBL_PA_RESOURCES.STATION_EVENTS
            }),
        enabled: Boolean(eventId)
    });

    const eventSummary = {
        "Station ID": eventsResponseData?.response?.stationId ?? '-',
        "Station Name": eventsResponseData?.response?.stationName ?? '-',
        Zone: eventsResponseData?.response?.zoneName ?? '-',
        "Timestamp": eventsResponseData?.response?.eventTimestamp ?? '-',
        "Project": eventsResponseData?.response?.division ?? '-',
    };

    const eventDetails = eventsResponseData?.response?.eventDetailsMap;

    const eventRootCauseSummary = {
        "Signal Name": eventDetails?.signal_name ?? '-',
        "Signal ID": eventDetails?.signal_id ?? '-',
        "Raw Aspect": eventDetails?.raw_aspect ?? '-',
        "Proving Aspect": eventDetails?.proving_aspect ?? '-',
        "Mismatch": eventDetails?.mismatch_duration_sec ?? '-',
        "Recovery": eventDetails?.recovery ?? '-',
        "Dependent Tracks": eventDetails?.dependent_tracks?.length ? eventDetails?.dependent_tracks?.join(', ') : '-',
        "Next Signal Name": eventDetails?.next_signal_name ?? '-',
        "Next Signal ID": eventDetails?.next_signal_id ?? '-',
        "Next Signal Raw Aspect": eventDetails?.next_signal_raw_aspect ?? '-',
        // "Entry timestamp (T₀)": eventDetails?.t0 ?? '-',


        // "Station ID": eventDetails?.station_id ?? '-',
        // "Signal Column": eventDetails?.signal_column ?? '-',

        // "Reporting Card": eventDetails?.reporting_card ?? '-',
        // "RF Signal Type": eventDetails?.rf_signal_type ?? '-',
        // "Event Sync Status": eventDetails?.event_sync_status ?? '-',

        // "Applicable Direction": eventDetails?.applicable_direction ?? '-',
        // "Signal Proving Message": eventDetails?.signal_proving_message ?? '-',
        // "Signal Proving Analysis": eventDetails?.signal_proving_analysis ?? '-',

        // "Evidence Summary": eventDetails?.evidence_summary ?? '-',
        // "Final RCA": eventDetails?.final_rca ?? '-',
    };

    const [isPrinting, setIsPrinting] = useState(false);

    const printRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `STATION_RCA_Event_${eventId}`,

        onBeforePrint: () =>
            new Promise<void>((resolve) => {


                const isDark = document.documentElement.classList.contains("dark");

                if (isDark) {
                    printRef.current?.classList.add("print-dark");
                }
                setIsPrinting(true);
                setTimeout(() => {
                    resolve();
                }, 1000);
            }),

        onAfterPrint: () => {
            printRef.current?.classList?.remove("print-dark");
            setIsPrinting(false);
        },

        onPrintError: (error) => {
            console.error("Print error:", error);
            printRef.current?.classList?.remove("print-dark");
        }
    });



    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">
            <div className="flex items-center gap-1">
                <IconArrowLeft
                    onClick={() => { handleNavigateToDashboard(); }}
                    className="cursor-pointer"
                />
                <BreadCrumb model={breadcrumb} className="p-2!" />
                <Button
                    label="Download"
                    disabled={isEventResponseLoading}
                    icon={<IconDownload size={20} />}
                    className="ml-auto mb-2 bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
                    onClick={handlePrint}
                />
            </div>

            <div ref={printRef}>
                <div className="flex items-center justify-between w-full my-3">
                    <h2 className="text-xl font-medium">Root Cause Analysis</h2>

                </div>

                {/* ---------- Event Summary ---------- */}
                <div className="grid grid-cols-1 mb-6">
                    <Card header="Event Summary">
                        {isEventResponseLoading ? (
                            <div className="p-4">
                                <Skeleton width="100%" height="100px" borderRadius="10px" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">

                                {Object.entries(eventSummary).map(([key, value]) => (
                                    <div
                                        key={key}
                                        className="flex items-center gap-2" // <-- stays horizontal
                                    >
                                        <span className="text-gray-500 whitespace-nowrap dark:text-gray-100">{key} :</span>
                                        <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
                                    </div>
                                ))}

                            </div>
                        )}
                    </Card>
                </div>


                {/* ---------- Root Cause Analysis Steps ---------- */}
                <div className="grid grid-cols-1 gap-4 mb-6">


                    <Card header="Root Cause">
                        <div className={`${isPrinting ? 'h-auto overflow-visible' : 'h-auto overflow-y-auto'}`}>
                            <Card
                                className="border border-red-200 rounded-xl shadow-sm"
                                pt={{
                                    root: {
                                        className: "p-2 mb-2"
                                    }
                                }}
                            >
                                {/* Header Row */}
                                <div className="flex items-start mb-2 gap-3">
                                    {isEventResponseLoading ? (
                                        <Skeleton width="100%" height="100px" borderRadius="10px" />
                                    ) : (
                                        <>
                                            {/* Left side: Icon + Title */}
                                            <div className="text-red-500 text-xl bg-red-50 flex items-center justify-center rounded-lg w-10 h-10">
                                                <IconAlertTriangle stroke={1.5} />
                                            </div>
                                            <div className="flex items-start gap-2 flex-1">


                                                <div className="flex flex-col">
                                                    <div className="text-sm">
                                                        <span className="font-medium">Event :</span>{" "}
                                                        <span className="font-semibold text-gray-800 dark:text-gray-100">
                                                            {eventsResponseData?.response?.eventCategory?.replace('_', ' ') ?? '-'}
                                                        </span>
                                                    </div>

                                                    {/* Date Time */}
                                                    <div className="text-gray-700 text-sm flex items-center gap-1">
                                                        <IconCalendar className="dark:text-gray-100" size={16} stroke={2} />
                                                        <span className="dark:text-gray-100">{eventSummary["Timestamp"]}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right-side badge */}
                                            <Tag
                                                severity={getSeverity(eventsResponseData?.response?.eventCategory?.replace('_', ' '))}
                                                value={eventsResponseData?.response?.eventCategory?.replace('_', ' ') ?? '-'}
                                                className="px-3! py-0!"
                                            />
                                        </>
                                    )}
                                </div>

                            </Card>

                            {isEventResponseLoading ? (
                                <div className="p-4">
                                    <Skeleton width="100%" height="150px" borderRadius="10px" />
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 p-4">

                                    {Object.entries(eventRootCauseSummary).map(([key, value]) => (
                                        <div
                                            key={key}
                                            className="flex items-start gap-2 min-w-0"
                                        >
                                            <span className="text-gray-500 whitespace-nowrap dark:text-gray-100">
                                                {key} :
                                            </span>

                                            <span className="font-medium text-gray-900 dark:text-gray-100 break-words">
                                                {value}
                                            </span>
                                        </div>
                                    ))}

                                    {/* Full width */}
                                    {/* <div className="flex items-start gap-2 min-w-0">
                                        <span className="text-gray-500 whitespace-nowrap dark:text-gray-100">
                                            Evidence Summary :
                                        </span>

                                        <span className="font-medium text-gray-900 dark:text-gray-100 break-words">
                                            {eventDetails?.evidence_summary ?? '-'}
                                        </span>
                                    </div> */}

                                    {/* Full width */}
                                    {/* <div className="flex items-start gap-2 min-w-0">
                                        <span className="text-gray-500 whitespace-nowrap dark:text-gray-100">
                                            Final RCA :
                                        </span>

                                        <span className="font-medium text-gray-900 dark:text-gray-100 break-words">
                                            {eventDetails?.final_rca ?? '-'}
                                        </span>
                                    </div> */}

                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 mb-6">
                    <Card header="Faulty Tracks">
                        <Table
                            columns={TABLE_COLUMNS_CONFIG.FAULTY_TRACKS}
                            data={eventsResponseData?.response?.eventDetailsMap?.faulty_tracks ?? []}
                            showGridlines={true}
                            paginator={false}
                            first={0}
                            rows={10}
                            isLoading={isEventResponseLoading}
                            enableScroll={true}
                            renderCell={(rowData, field) => {
                                return rowData[field] ?? '-';
                            }}
                        />
                    </Card>
                </div>


            </div>
        </div>
    );
}